import * as React from 'react';
import styled from 'styled-components';
import { makeStyles, Box } from '@material-ui/core';
import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import Button from '@material-ui/core/Button';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import { useSelector } from 'react-redux';
import ProjectFormStepOne from './ProjectFormStepOne';
import ProjectFormStepThree from './ProjectFormStepThree';
import ProjectFormStepFour from './ProjectFormStepFour';
import ProjectFormReview from './ProjectFormReview';
import { ButtonWithLoader, InputBaseContainer, InputSelectWithLabel, LoaderWithContainer, ModalDefaultDraggable } from '../molecules';
import { createProject, getExternalProjects, getProjects, ProjectType } from '../api/projects';
import { getContactTypeList } from '../api/contactType';
import { GET_ALL_QUERY_PARAMS } from '../constants';
import { USER_TYPES_VALUES, CLIENT_TYPES_VALUES } from '../constants/forms';
import ModalActionDialog from '../molecules/ModalActionDialog';
import useTablePage from '../hooks/useTablePage';
import UsersForm from './UsersForm';
import AddContactWizard from './AddContactWizard';
import { createUser, getUsers, getUsersCustom } from '../api/users';
import ModalInfo from '../molecules/ModalInfo';
import useUserRole from '../hooks/useUserRole';
import { getCurrentUser } from '../api/userSettings';
import useDidUpdateEffect from '../hooks/useDidUpdate';
import formatDate from '../utils/formatDate';
import { validationSchema } from './ProjectFormContainer/ProjectFormSchema';
import { RootState } from '../redux/root';
import { defaultValues } from './ProjectFormContainer/ProjectFormLabels';
import { dispatch } from '../redux/store';
import { persistNewProjectData } from '../utils';
import { useGetClientProjectsQuery } from '../redux/rtkQuery/apiSlice';
import { ButtonMain } from '../atoms';

const useStyles = makeStyles(theme => ({
  layout: {
    width: 'auto',
    marginLeft: theme.spacing(0.5),
    marginRight: theme.spacing(0.5),
    [theme.breakpoints.up(600 + theme.spacing(2) * 2)]: {
      width: '90%',
      marginLeft: 'auto',
      marginRight: 'auto',
    },
  },
  button: {
    marginRight: theme.spacing(1),
  },
  instructions: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  paper: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    [theme.breakpoints.up(600 + theme.spacing(3) * 2)]: {
      marginTop: theme.spacing(4),
      marginBottom: theme.spacing(6),
      padding: theme.spacing(3),
    },
  },
}));

const ProjectFormContainer = ({
  onSubmit,
  handleCloseMainForm,
  isModalError,
  setIsExistingProjectChosen,
  isExistingProjectChosen,
  setModalTitle,
}: Props) => {
  const {
    isModalOpen,
    modalTitle,
    values,
    modalErrorTitle,
    errors,
    handleAdd,
    modalError,
    modalSuccess,
    isApiSuccess,
    createOrUpdate,
    handleCloseModal,
    handleCloseModalInfo,
    setModalTitle: setContactModalTitle,
  } = useTablePage<ProjectType>({
    createEndpoint: createUser,
    modalCreateTitle: 'Add Individual',
  });

  const classes = useStyles();
  const [activeStep, setActiveStep] = React.useState(0);

  const [isContactModalOpen, setContactModalOpen] = React.useState(false);
  const [contacts, setContacts] = React.useState(null);
  const [selectedContacts, setSelectedContacts] = React.useState(null);
  const [savedContacts, setSavedContacts] = React.useState(null);
  const [contactTypes, setContactTypes] = React.useState(null);

  const steps = ['Address', 'Project Details', 'Permit', 'Review'];

  const [projectsList, setProjectsList] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
  const [isSomeStepLoading, setIsSomeStepLoading] = React.useState(false);
  const [isNewProject, setIsNewProject] = React.useState([]);
  const [isDialogModalOpen, setIsDialogModalOpen] = React.useState(false);
  const [isChooseProjectModalOpen, setIsChooseProjectModalOpen] = React.useState(false);
  const [selectedProject, setSelectedProject] = React.useState(null);
  const [isContactFormSubmitting, setIsContactFormSubmitting] = React.useState(false);
  const projectCandidate = useSelector((s:RootState) => s.projectsSlice.projectCandidate);
  const { data: clientProject, refetch } = useGetClientProjectsQuery({ project_request: true });

  const { isEmployee } = useUserRole();

  const [currentManager, setCurrentManager] = React.useState(null);
  const [conflictModalOpen, setConflictModalOpen] = React.useState(false);
  const [conflictingFranchiseName, setConflictingFranchiseName] = React.useState('');

  useDidUpdateEffect(() => {
    getContactTypeList({
      ...GET_ALL_QUERY_PARAMS,
    }).then(contactTypeResponse => {
      setContactTypes(contactTypeResponse.data.results?.filter(el => el?.isActiveStatus === 'True'));
    }).catch(() => {});
  }, [activeStep === 2]);

  const [isContactsLoading, setIsContactsLoading] = React.useState(false);
  const contactSearchTimer = React.useRef<any>(null);

  const searchContacts = (inputValue: string) => {
    if (contactSearchTimer.current) {
      clearTimeout(contactSearchTimer.current);
    }
    if (!inputValue || inputValue.length < 2) {
      setContacts([]);
      return;
    }
    contactSearchTimer.current = setTimeout(() => {
      setIsContactsLoading(true);
      getUsersCustom({
        userRole: USER_TYPES_VALUES.CLIENT,
        project: 'true',
        search: inputValue,
      }).then(individualClientsResponse => {
        setContacts([
          ...individualClientsResponse.data?.filter(el => el?.isActive),
        ]);
      })
      .catch(() => {})
      .finally(() => setIsContactsLoading(false));
    }, 300);
  };

  React.useEffect(() => {
    if (activeStep === 2) {
      setModalTitle('Add Permit');
    } else if (activeStep === 3) {
      setModalTitle('Review');
    } else {
      setModalTitle('Add Project');
    }
  }, [activeStep]);

  // currentManager auto-selection moved into ProjectFormStepThree

  const currentValidationSchema = validationSchema[activeStep];

  const methods = useForm({
    shouldUnregister: false,
    defaultValues: projectCandidate || defaultValues,
    resolver: yupResolver(currentValidationSchema),
    mode: 'all',
  });

  const { handleSubmit, trigger, getValues, formState, setError, errors: formErrors } = methods;

  React.useEffect(() => {
    if (formState.isSubmitting && !Object.keys(formErrors).length) {
      setIsSubmitting(formState.isSubmitting);
    }
  }, [formState]);

  React.useEffect(() => {
    if (Object.keys(formErrors).length) {
      setIsSubmitting(false);
    }
  }, [formErrors]);

  const handleCloseForm = () => {
    if (!modalError && !modalSuccess) {
      handleCloseModal();
    }
  };

  const handleCloseContactForm = () => {
    setSelectedContacts(null);
    setContactModalOpen(false);
  };

  const handleSelectContacts = selected => {
    methods.setValue('selectContactsField', selected);
    setSelectedContacts(selected);
    if (!selected && selectedContacts) {
      setSelectedContacts(null);
    }
  };

  const handleSaveContacts = () => {
    if (savedContacts && selectedContacts) {
      const contactsAll = [...savedContacts, ...selectedContacts];
      const uniqueContacts = contactsAll.reduce((x, y) => (x.findIndex(e => e.id === y.id) < 0 ? [...x, y] : x), []);
      setSavedContacts(uniqueContacts);
      setSelectedContacts(null);
    }
    if (!savedContacts) {
      setSavedContacts(selectedContacts);
    }
    setContactModalOpen(false);
  };

  let isStepValid = true;

  const [isValidating, setIsValidating] = React.useState(false);

  const validate = async () => {
    setIsValidating(true);
    isStepValid = await trigger();
    setIsValidating(false);
    if (!isStepValid) return false;
    return true;
  };

  const handleNext = async () => {
    const pattern = /^\s+/g;

    if (activeStep === 0) {
      const isValid = await trigger();
      if (!isValid) return;
      const address = await methods.getValues('address');
      const city = await methods.getValues('city');
      const state = await methods.getValues('state');
      const street = await methods.getValues('street');
      let hasSpaceError = false;

      if (pattern.test(address)) {
        setError('address', {
          type: 'manual',
          message: 'Address field contains too many spaces',
        });
        hasSpaceError = true;
      }

      if (pattern.test(city)) {
        setError('city', {
          type: 'manual',
          message: 'City field contains too many spaces',
        });
        hasSpaceError = true;
      }

      if (pattern.test(state)) {
        setError('state', {
          type: 'manual',
          message: 'State field contains too many spaces',
        });
        hasSpaceError = true;
      }

      if (pattern.test(street)) {
        setError('street', {
          type: 'manual',
          message: 'Street field contains too many spaces',
        });
        hasSpaceError = true;
      }

      if (hasSpaceError) return;
    }

    if (activeStep === 1) {
      const isValid = await trigger();
      if (!isValid) return;
      const projectName = getValues('name');

      if (pattern.test(projectName)) {
        setError('name', {
          type: 'manual',
          message: 'Project name field contains too many spaces',
        });
        return;
      }
    }

    if (activeStep === 2) {
      const isValid = await trigger();
      if (!isValid) return;
      const permitName = getValues('permitName');
      const description = getValues('description');

      if (pattern.test(permitName)) {
        setError('permitName', {
          type: 'manual',
          message: 'Permit name field contains too many spaces',
        });
        return;
      }

      if (pattern.test(description)) {
        setError('description', {
          type: 'manual',
          message: 'Description field contains too many spaces',
        });
        return;
      }
    }

    if (!Object.keys(formState?.errors)?.length) {
      const res = await validate();

      if (res) {
        if (activeStep === 0) {
          const externalProjectResponse = await getExternalProjects({
            is_deleted: false,
            search: getValues('address'),
          });

          const externalProjects = externalProjectResponse?.data?.results;

          if (externalProjects?.length > 0) {
            const franchiseName = externalProjects[0]?.franchiseName || 'branch';
            setConflictingFranchiseName(franchiseName);
            setConflictModalOpen(true); // Open custom modal
            return;
          }

          const projectResponse = await getProjects({
            is_deleted: false,
            search: getValues('address'),
          });

          if (projectResponse?.data?.results?.length) {
            setIsNewProject(projectResponse?.data?.results);
            setIsDialogModalOpen(true);
          }
        }

        setActiveStep(prev => prev + 1);
      }
    }
  };

  const handleBack = () => {
    methods.clearErrors();
    setActiveStep(prevActiveStep => prevActiveStep - 1);
  };

  function getStepContent(step) {
    switch (step) {
      case 0:
        return <ProjectFormStepOne currentValidationSchema={currentValidationSchema} />;
      case 1:
        return (
          <ProjectFormStepThree
            isContactFormSubmitting={isApiSuccess}
            setIsContactFormSubmitting={setIsContactFormSubmitting}
            currentValidationSchema={currentValidationSchema}
            setIsSecondModalOpen={handleAdd}
            setIsSomeStepLoading={setIsSomeStepLoading}
            selectedProject={selectedProject}
          />
        );
      case 2:
        return (
          <ProjectFormStepFour
            isContactFormSubmitting={isApiSuccess}
            setIsContactFormSubmitting={setIsContactFormSubmitting}
            currentValidationSchema={currentValidationSchema}
            setIsSecondModalOpen={handleAdd}
            setIsContactModalOpen={handleAdd}
            setIsSomeStepLoading={setIsSomeStepLoading}
            setContactModalOpen={setContactModalOpen}
            selectedContacts={savedContacts}
            setSelectedContacts={setSavedContacts}
            contactTypes={contactTypes}
          />
        );
      case 3:
        return (
          <ProjectFormReview
            onEditStep={(targetStep: number) => setActiveStep(targetStep)}
            savedContacts={savedContacts}
            contactTypes={contactTypes}
          />
        );
      default:
        return 'Unknown step';
    }
  }

  const handleSelectedProjectContacts = (projectContacts, allContacts, permitContacts) => {
    if (!projectContacts.length) {
      return permitContacts?.filter(contact => !!contact?.id) || [];
    }
    return allContacts.filter(contact => savedContacts?.map(el => el?.id)?.includes(contact.id));
  };

  const middleware = (submitFn: (data) => void) => data => {
    const parsedInspectionsCount =
      data.inspectionsNumber === '' || data.inspectionsNumber === null || data.inspectionsNumber === undefined
        ? null
        : Number(data.inspectionsNumber);
    const hasInspectionsCount = Number.isFinite(parsedInspectionsCount as number);
    const inspectionsCountField = hasInspectionsCount
      ? { inspections_count: parsedInspectionsCount }
      : {};
    const payload1 = {
      ...data,
      expirationDate: data.expirationDate ? `${formatDate(data.expirationDate)}` : null,
      name: data.name,
      contacts: data.projectContacts ? data.projectContacts?.filter(item => item.id).map(item => item.id) : undefined,
      manager: data.manager?.id,
      jurisdiction: data.jurisdiction?.id,
      // franchise: data.franchise?.id,
      permit: {
        amount: data.totalContract ? data.totalContract : '0',
        description: data.description,
        // @ts-ignore
        client_attachments: projectCandidate?.attachments?.length > 0 ? projectCandidate?.attachments : [],
        name: data.permitName,
        expirationDate: data.expirationDate ? `${formatDate(data.expirationDate)}` : null,
        building: data.building?.id,
        contractor: data.contractor?.id,
        ...inspectionsCountField,
        manager: data.permitManager?.id,
        work: data.workScope?.id,
        serviceType: data.serviceType?.filter(item => item.id).map(item => item.id),
        contacts: savedContacts
          ?.map(contact => {
            const id = +contact.id;
            const isCompany = Number(contact?.clientType) === CLIENT_TYPES_VALUES.COMPANY;
            return {
              ...(isCompany ? { company_user: id } : { user: id }),
              contact_type: contact.contactType ? +contact.contactType : +contactTypes[0]?.id,
            };
          })
          .filter(el => !!(el.user || el.company_user)),
      },
    };

    const payload2 = {
      name: data?.permitName,
      description: data.description,
      expirationDate: data.expirationDate ? `${formatDate(data.expirationDate)}` : null,
      contractor: data.contractor?.id,
      amount: data.totalContract ? data.totalContract : '0',
      building: data.building?.id,
      manager: data.permitManager?.id,
      work: data.workScope?.id,
      ...inspectionsCountField,
      serviceType: data.serviceType?.filter(item => item.id).map(item => item.id),
      project: selectedProject ? selectedProject.id : null,
      // jurisdiction: selectedProject?.jurisdiction,
      contacts: handleSelectedProjectContacts(selectedProject ? selectedProject.contacts : [], contacts, savedContacts)
        .map(contact => {
          const id = +contact.id;
          const isCompany = Number(contact?.clientType) === CLIENT_TYPES_VALUES.COMPANY;
          return {
            ...(isCompany ? { company_user: id } : { user: id }),
            contact_type: contact.contactType ? +contact.contactType : +contactTypes[0]?.id,
          };
        })
        .filter(el => !!(el.user || el.company_user)),
    };
    try {
      if (projectCandidate) {
        persistNewProjectData(projectCandidate, selectedProject, payload1, payload2, dispatch, refetch);
        return;
      } return submitFn(
        !isExistingProjectChosen && !projectCandidate
          ? payload1
          : payload2,
      );
    } catch (error) {
      //
    } finally {
      setIsExistingProjectChosen(false);
    }
  };

  const submitWrapper = async func => {
    if (activeStep === 3) {
      func();
    }
  };

  if (isLoading) {
    return <LoaderWithContainer />;
  }

  return (
    <>
      <div className={classes.layout}>
        <StepperWrap>
          {steps.map((label, index) => {
            const isCompleted = index < activeStep;
            const isActive = index === activeStep;
            return (
              <React.Fragment key={label}>
                {index > 0 && <StepLine $completed={isCompleted} />}
                <StepItem>
                  <StepCircle $completed={isCompleted} $active={isActive}>
                    {isCompleted ? '✓' : index + 1}
                  </StepCircle>
                  <StepText $active={isActive} $completed={isCompleted}>
                    {label}
                  </StepText>
                </StepItem>
              </React.Fragment>
            );
          })}
        </StepperWrap>
        <div style={{ minHeight: '50%' }}>
          {activeStep !== steps.length && !isValidating ? (
            // eslint-disable-next-line react/jsx-props-no-spreading
            <FormProvider {...methods}>
              <div className={classes.instructions}>{getStepContent(activeStep)}</div>
              <Box display="flex" justifyContent="center" style={{ paddingTop: '5vh' }}>
                {activeStep !== 0 && (
                  <Button
                    disabled={activeStep === 0 || isLoading || isSomeStepLoading || !!selectedProject}
                    onClick={handleBack}
                    className={classes.button}
                  >
                    Back
                  </Button>
                )}
                {activeStep === steps.length - 1 ? (
                  <ButtonWithLoader
                    variant="contained"
                    color="primary"
                    onClick={() => submitWrapper(handleSubmit(middleware(onSubmit)))}
                    className={classes.button}
                    disabled={(isSubmitting && !isModalError) || !isStepValid || isLoading || isSomeStepLoading}
                    loading={isSubmitting && !isModalError}
                  >
                    Submit
                  </ButtonWithLoader>
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleNext}
                    className={classes.button}
                    disabled={!isStepValid || isLoading || isSomeStepLoading}
                  >
                    Next
                  </Button>
                )}
              </Box>
            </FormProvider>
          ) : (
            <LoaderWithContainer />
          )}
        </div>
      </div>

      {conflictModalOpen && (
      <ModalDefaultDraggable
        title="Project Conflict"
        onClose={() => setConflictModalOpen(false)}
        centered
        wide
      >
        <div style={{ textAlign: 'center' }}>
          <p>
            There is already a project with this address in <strong>{conflictingFranchiseName}</strong>.
            <br />
            Please check with the branch before continuing.
          </p>
          <div style={{ marginTop: '24px' }}>
            <ButtonMain onClick={() => setConflictModalOpen(false)} color="primary">
              OK
            </ButtonMain>
          </div>
        </div>
      </ModalDefaultDraggable>
      )}

      {isDialogModalOpen && (
        <ModalActionDialog
          title="Is it going to be a new project or this is an addition to an existing project?"
          className="over"
          centered
          onCancel={() => {
            handleCloseMainForm();
            setIsDialogModalOpen(false);
          }}
          actionFirst={() => {
            setIsExistingProjectChosen(true);
            setIsChooseProjectModalOpen(true);
            setIsDialogModalOpen(false);
          }}
          actionSecond={() => {
            setIsNewProject(null);
            setActiveStep(1);
            setIsDialogModalOpen(false);
          }}
          buttonFirstTitle="Existing project"
          buttonSecondTitle="New project"
        />
      )}

      {isChooseProjectModalOpen && (
        <ModalActionDialog
          title="Select Project"
          className="over"
          onCancel={() => {
            handleCloseMainForm();
            setIsExistingProjectChosen(false);
          }}
          actionFirst={() => {
            setActiveStep(2);
            setIsChooseProjectModalOpen(false);
          }}
          actionSecond={null}
          buttonFirstDisable={!selectedProject}
          buttonFirstTitle="Select"
        >
          <InputBaseContainer>
            <InputSelectWithLabel
              placeholder=""
              menuPlacement="bottom"
              label="Project Name"
              options={isNewProject}
              onChange={selected => setSelectedProject(selected)}
              getOptionValue={(item: any) => item.id || Math.random()}
              getOptionLabel={(item: any) => item.name}
              isRequired
            />
          </InputBaseContainer>
        </ModalActionDialog>
      )}

      {isModalOpen && (
        <StyledModalDraggable onClose={handleCloseForm} title={modalTitle} wide>
          {values ? (
            <UsersForm
              setIsContactFormSubmitting={setIsContactFormSubmitting}
              initialValues={values}
              modalError={modalErrorTitle.length > 0}
              initialErrors={errors}
              onSubmit={createOrUpdate}
            />
          ) : (
            <AddContactWizard
              modalError={modalErrorTitle.length > 0}
              initialErrors={errors}
              onSubmit={createOrUpdate}
              setModalTitle={setContactModalTitle}
            />
          )}
        </StyledModalDraggable>
      )}

      {isContactModalOpen && (
        <ModalDefaultDraggable
          title="Select Contact"
          onClose={handleCloseContactForm}
          centered
        >
          <ContactPickerWrap>
            <ContactSearchInput
              placeholder="Search contacts by name or email (min 2 characters)..."
              onChange={e => searchContacts(e.target.value)}
              autoFocus
            />
            {isContactsLoading && (
              <ContactPickerStatus>Searching...</ContactPickerStatus>
            )}
            {!isContactsLoading && contacts && contacts.length > 0 && (
              <ContactResultsList>
                {(contacts || []).map(contact => {
                  const isSelected = selectedContacts?.some(c => c.id === contact.id);
                  const name = [contact.firstName, contact.lastName].filter(Boolean).join(' ');
                  const detail = [contact.email, contact.companyName || contact.parentCompany?.companyName].filter(Boolean).join(' - ');
                  return (
                    <ContactResultItem
                      key={contact.id}
                      $selected={isSelected}
                      onClick={() => {
                        if (isSelected) {
                          const filtered = selectedContacts.filter(c => c.id !== contact.id);
                          setSelectedContacts(filtered.length ? filtered : null);
                        } else {
                          setSelectedContacts([...(selectedContacts || []), contact]);
                        }
                      }}
                    >
                      <ContactCheckbox $checked={isSelected}>{isSelected && '✓'}</ContactCheckbox>
                      <ContactInfo>
                        <ContactName>{name || contact.companyName || 'Unknown'}</ContactName>
                        {detail && <ContactDetail>{detail}</ContactDetail>}
                      </ContactInfo>
                    </ContactResultItem>
                  );
                })}
              </ContactResultsList>
            )}
            {!isContactsLoading && contacts && contacts.length === 0 && (
              <ContactPickerStatus>No contacts found</ContactPickerStatus>
            )}
            {selectedContacts && selectedContacts.length > 0 && (
              <SelectedChipsWrap>
                <SelectedLabel>Selected ({selectedContacts.length}):</SelectedLabel>
                <SelectedChips>
                  {selectedContacts.map(c => {
                    const chipName = [c.firstName, c.lastName].filter(Boolean).join(' ') || c.companyName || c.email;
                    return (
                      <Chip key={c.id}>
                        {chipName}
                        <ChipRemove onClick={() => {
                          const filtered = selectedContacts.filter(sc => sc.id !== c.id);
                          setSelectedContacts(filtered.length ? filtered : null);
                        }}>&times;</ChipRemove>
                      </Chip>
                    );
                  })}
                </SelectedChips>
              </SelectedChipsWrap>
            )}
            <NoContactPrompt>
              Can&apos;t find your contact?
              <AddNewContactLink
                type="button"
                onClick={() => {
                  setContactModalOpen(false);
                  handleAdd();
                }}
              >
                Add new contact
              </AddNewContactLink>
            </NoContactPrompt>
            <ContactPickerActions>
              <ButtonMain
                color="primary"
                onClick={handleSaveContacts}
                disabled={!selectedContacts || !selectedContacts.length}
              >
                Add Selected
              </ButtonMain>
              <ButtonMain
                color="default"
                onClick={handleCloseContactForm}
              >
                Cancel
              </ButtonMain>
            </ContactPickerActions>
          </ContactPickerWrap>
        </ModalDefaultDraggable>
      )}

      {(modalSuccess || modalError) && (
        <ModalInfo timeToClose={5000} isError={modalError} onClose={handleCloseModalInfo} errorMsg={modalErrorTitle} />
      )}
    </>
  );
};

const StepperWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-6) var(--space-4);
`;

const StepItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2);
  position: relative;
  z-index: 1;
`;

const StepCircle = styled.div<{ $completed: boolean; $active: boolean }>`
  width: 36px;
  height: 36px;
  border-radius: var(--radius-full);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--fs-sm);
  font-weight: var(--fw-semibold);
  transition: all var(--motion-base) var(--ease-standard);

  ${({ $completed, $active }) =>
    $completed
      ? `
        background: var(--success-500);
        color: var(--neutral-0);
        border: 2px solid var(--success-500);
      `
      : $active
      ? `
        background: var(--primary-600);
        color: var(--neutral-0);
        border: 2px solid var(--primary-600);
        box-shadow: 0 0 0 4px var(--primary-100);
      `
      : `
        background: var(--neutral-0);
        color: var(--neutral-500);
        border: 2px solid var(--neutral-300);
      `}
`;

const StepText = styled.div<{ $active: boolean; $completed: boolean }>`
  font-size: var(--fs-sm);
  font-weight: ${({ $active }) => $active ? 'var(--fw-semibold)' : 'var(--fw-medium)'};
  color: ${({ $active, $completed }) =>
    $active ? 'var(--primary-700)' : $completed ? 'var(--success-600)' : 'var(--neutral-400)'};
  white-space: nowrap;
`;

const StepLine = styled.div<{ $completed: boolean }>`
  flex: 1;
  height: 2px;
  min-width: 60px;
  margin: 0 var(--space-2);
  margin-bottom: var(--space-6);
  background: ${({ $completed }) =>
    $completed ? 'var(--success-500)' : 'var(--neutral-300)'};
  transition: background-color var(--motion-base) var(--ease-standard);
`;

const ContactPickerWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  width: 100%;
`;

const ContactSearchInput = styled.input`
  width: 100%;
  height: 40px;
  padding: 0 var(--space-3);
  border: 1px solid var(--neutral-300);
  border-radius: var(--radius-md);
  font-size: var(--fs-base);
  font-family: inherit;
  color: var(--neutral-800);
  background: var(--neutral-0);
  transition: border-color var(--motion-fast) var(--ease-standard),
    box-shadow var(--motion-fast) var(--ease-standard);

  &::placeholder {
    color: var(--neutral-400);
  }
  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: var(--shadow-focus);
  }
`;

const ContactPickerStatus = styled.div`
  text-align: center;
  color: var(--neutral-500);
  font-size: var(--fs-sm);
  padding: var(--space-3) 0;
`;

const ContactResultsList = styled.div`
  max-height: 240px;
  overflow-y: auto;
  border: 1px solid var(--neutral-200);
  border-radius: var(--radius-md);
`;

const ContactResultItem = styled.div<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-3);
  cursor: pointer;
  border-bottom: 1px solid var(--neutral-100);
  background: ${({ $selected }) => $selected ? 'var(--primary-50)' : 'var(--neutral-0)'};
  transition: background-color var(--motion-fast) var(--ease-standard);

  &:last-child { border-bottom: 0; }
  &:hover { background: ${({ $selected }) => $selected ? 'var(--primary-100)' : 'var(--neutral-50)'}; }
`;

const ContactCheckbox = styled.div<{ $checked: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: var(--radius-sm);
  border: 2px solid ${({ $checked }) => $checked ? 'var(--primary-600)' : 'var(--neutral-400)'};
  background: ${({ $checked }) => $checked ? 'var(--primary-600)' : 'transparent'};
  color: var(--neutral-0);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  flex-shrink: 0;
  transition: all var(--motion-fast) var(--ease-standard);
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const ContactName = styled.div`
  font-size: var(--fs-base);
  font-weight: var(--fw-medium);
  color: var(--neutral-800);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ContactDetail = styled.div`
  font-size: var(--fs-sm);
  color: var(--neutral-500);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SelectedChipsWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
`;

const SelectedLabel = styled.div`
  font-size: var(--fs-sm);
  font-weight: var(--fw-semibold);
  color: var(--neutral-600);
`;

const SelectedChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
`;

const Chip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  padding: var(--space-1) var(--space-3);
  background: var(--primary-50);
  border: 1px solid var(--primary-200);
  border-radius: var(--radius-full);
  font-size: var(--fs-sm);
  color: var(--primary-800);
`;

const ChipRemove = styled.span`
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  color: var(--primary-400);
  margin-left: var(--space-1);

  &:hover { color: var(--danger-500); }
`;

const ContactPickerActions = styled.div`
  display: flex;
  justify-content: center;
  gap: var(--space-3);
  padding-top: var(--space-2);
`;

const NoContactPrompt = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  padding: var(--space-3) 0;
  font-size: var(--fs-sm);
  color: var(--neutral-600);
  border-top: 1px solid var(--neutral-200);
`;

const AddNewContactLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  font-weight: var(--fw-semibold);
  color: var(--primary-600);
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: var(--primary-700);
  }
  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
    border-radius: var(--radius-sm);
  }
`;

const StyledModalDraggable = styled(ModalDefaultDraggable)`
  height: 100%;
  overflow: inherit;
`;

interface Props {
  onSubmit: (data) => void;
  setModalTitle: (title: string) => void;
  handleCloseMainForm: any;
  isModalError: boolean;
  isExistingProjectChosen: boolean;
  setIsExistingProjectChosen: (data: boolean) => void;
}

export default ProjectFormContainer;
